import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { 
  Facebook as FacebookIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const OAuthSelection: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleFacebookLogin = () => {
    const baseUrl = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:8080';
    window.location.href = `${baseUrl}/auth/facebook`;
  };




  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(0deg, #f1fff9, #f1fff900 30%), radial-gradient(circle at 0 0, #5fd5b6 0, #5fd5b600 30%), linear-gradient(180deg, #79dfffb3 -10%, #79dfff00 70%), radial-gradient(circle at 95% 0, #d0f224 0, #5fd5b600 20%)',
        position: 'relative',
      }}
    >
      {/* Logout Button */}
      <Box
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 1000,
        }}
      >
        <Tooltip title="Logout" arrow>
          <IconButton
            onClick={handleLogout}
            disabled={loading}
            sx={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.95)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {loading ? <CircularProgress size={24} /> : <LogoutIcon sx={{ color: '#666' }} />}
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        elevation={8}
        sx={{
          p: 6,
          width: '100%',
          maxWidth: 600,
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(95, 213, 182, 0.2)',
          boxShadow: '0 8px 32px rgba(95, 213, 182, 0.15)',
          borderRadius: 3,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Connect Your Social Media
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontWeight: 300 }}>
          Easily manage conversations, respond to customers in real time, and grow your brand presence — all by linking your social media account. Select a platform to get started and simplify your communication workflow.
        </Typography>

        {/* Platform Icons - Facebook login ile yönetilebilen servisler */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: 3, 
          mb: 4,
          px: 4 
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 1 
          }}>
            <Box sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: '50%', 
              background: '#1877F2', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(24, 119, 242, 0.3)'
            }}>
              <svg viewBox="0 0 24 24" fill="white" style={{ width: 24, height: 24 }}>
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Facebook
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 1 
          }}>
            <Box sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: '50%', 
              background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(220, 39, 67, 0.3)'
            }}>
              <svg viewBox="0 0 24 24" fill="white" style={{ width: 24, height: 24 }}>
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Instagram
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 1 
          }}>
            <Box sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: '50%', 
              background: '#25D366', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)'
            }}>
              <svg viewBox="0 0 24 24" fill="white" style={{ width: 24, height: 24 }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              WhatsApp
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 1 
          }}>
            <Box sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: '50%', 
              background: '#000', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}>
              {/* TikTok SVG icon */}
              <svg viewBox="0 0 48 48" fill="none" style={{ width: 24, height: 24 }}>
                <g>
                  <path d="M41.5 17.5c-3.6 0-6.5-2.9-6.5-6.5V6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#25F4EE"/>
                  <path d="M41.5 19.5c-3.6 0-6.5-2.9-6.5-6.5V8h-4.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-4.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V25.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#FE2C55"/>
                  <path d="M35 13c0 3.6 2.9 6.5 6.5 6.5v-4c-1.9 0-3.5-1.6-3.5-3.5H35z" fill="#25F4EE"/>
                  <path d="M35 11c0 3.6 2.9 6.5 6.5 6.5v-2c-2.5 0-4.5-2-4.5-4.5H35z" fill="#FE2C55"/>
                  <path d="M35 6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8c-3.6 0-6.5-2.9-6.5-6.5V6z" fill="#000"/>
                </g>
              </svg>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              TikTok
            </Typography>
          </Box>


        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontWeight: 400, textAlign: 'center', fontStyle: 'italic' }}>
          Connect your Facebook account to manage messages from all these platforms in one place
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 4 }}>
          <Button
            variant="contained"
            size="medium"
            startIcon={<FacebookIcon />}
            onClick={handleFacebookLogin}
            sx={{ 
              py: 1.5,
              fontSize: '1rem',
              borderRadius: 2,
              fontWeight: 500,
              background: '#1877F2',
              '&:hover': {
                background: '#1666cf',
              },
            }}
          >
            Connect Facebook Page
          </Button>



          {/* Instagram OAuth removed; IG messaging handled via connected Facebook Page */}
        </Box>
        
        {/* Copyright */}
        <Box sx={{ 
          position: 'fixed', 
          bottom: 20, 
          left: 0, 
          right: 0, 
          textAlign: 'center',
          zIndex: 1000
        }}>
          <Typography 
            variant="body2" 
            color="rgba(128, 128, 128, 0.8)" 
            sx={{ 
              fontWeight: 300,
              fontSize: '0.75rem',
              letterSpacing: '0.3px'
            }}
          >
            © 2025 NowUp. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default OAuthSelection; 